import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import { collection, addDoc, doc, getDocs, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import Modal from 'react-modal';
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import * as XLSX from 'xlsx'; // For Excel file parsing
import Papa from 'papaparse'; // For CSV file parsing

Modal.setAppElement('#root');

const Subjects = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [newSubject, setNewSubject] = useState({ name: '', code: '' });
  const [editingSubject, setEditingSubject] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [isFetchingSubjects, setIsFetchingSubjects] = useState(true);
  const [searchSubject, setSearchSubject] = useState('');
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel'); // 'excel' or 'csv'
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');

  const subjectsCollectionRef = collection(db, "subjects");

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    setIsFetchingSubjects(true);
    try {
      const data = await getDocs(subjectsCollectionRef);
      setSubjects(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      setError("Failed to fetch subjects");
      setShowModal(true);
    } finally {
      setIsFetchingSubjects(false);
    }
  };

  const filteredSubjects = subjects.filter(subject =>
    subject.name.toLowerCase().includes(searchSubject.toLowerCase()) ||
    subject.code.toLowerCase().includes(searchSubject.toLowerCase())
  );  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSubject({ ...newSubject, [name]: value });
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
  };

  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setImportStatus('Processing file...');
    setImportProgress(0);

    try {
      let subjectsData = [];

      if (fileType === 'excel') {
        // Process Excel file
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        subjectsData = XLSX.utils.sheet_to_json(worksheet);
      } else {
        // Process CSV file
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true
        });
        subjectsData = result.data;
      }

      if (subjectsData.length === 0) {
        throw new Error("No subject data found in the file");
      }

      setImportStatus(`Importing ${subjectsData.length} subjects...`);

      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < subjectsData.length; i++) {
        try {
          const subject = subjectsData[i];

          // Validate required fields
          if (!subject.name || !subject.code) {
            errorCount++;
            continue;
          }

          // Create Firestore document
          const subjectData = {
            name: subject.name.trim(),
            code: subject.code.trim(),
            createdAt: Timestamp.now()
          };

          await addDoc(subjectsCollectionRef, subjectData);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error importing subject ${i + 1}:`, error);
        }

        // Update progress
        setImportProgress(Math.round(((i + 1) / subjectsData.length) * 100));
      }

      setImportStatus(`Import complete: ${successCount} successful, ${errorCount} failed`);
      setSuccessMessage(`Bulk import completed: ${successCount} subjects added successfully`);
      fetchSubjects();
    } catch (error) {
      setError(`Bulk import failed: ${error.message}`);
      setShowModal(true);
    } finally {
      setUpdate(false);
      setFile(null);
      setTimeout(() => {
        setIsBulkImportModalOpen(false);
        setImportProgress(0);
        setImportStatus('');
      }, 2000);
    }
  };

  const handleAddSubject = async () => {
    setError("");
    setSuccessMessage("") ;

    const requiredFields = {
      name: "Subject name",
      code: "Subject code",
    };
    for (const [field, displayName] of Object.entries(requiredFields)) {
      const value = field === "name" ? newSubject[field].trim() : newSubject[field];
      if (!value) {
        setError(`${displayName} cannot be empty`);
        setShowModal(true);
        return;
      }
    }  
    setUpdate(true);
    setError("");
    setSuccessMessage("");
    try {
      await addDoc(subjectsCollectionRef, {
        ...newSubject,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Subject added successfully");
      fetchSubjects();
      setNewSubject({ name: '', code: ''});
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add subject");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateSubject = async () => {
    setUpdate(true);
    try {
      const subjectDoc = doc(db, "subjects", editingSubject.id);
      await updateDoc(subjectDoc, newSubject);
      setSuccessMessage("Subject updated successfully");
      fetchSubjects();
      setEditingSubject(null);
      setNewSubject({ name: '', code: '' });
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update subject");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditSubject = (subjectToEdit) => {
    setEditingSubject(subjectToEdit);
    setNewSubject({ name: subjectToEdit.name, code: subjectToEdit.code });
    setIsEditModalOpen(true);
  };

  const handleDeleteSubject = async (id) => {
    setUpdate(true);
    try {
      const subjectDoc = doc(db, "subjects", id);
      await deleteDoc(subjectDoc);
      setSuccessMessage("Subject deleted successfully");
      fetchSubjects();
    } catch (error) {
      setError("Failed to delete subject");
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  const openDeleteModal = (subject) => {
    setCurrentSubject(subject);
    setIsDeleteModalOpen(true);
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  if (!userData) {
    return <Loader text={"READING"} />;
  }

  const allowedRoles = ['Admin', 'Cashier'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <div className="column align-center justify-center">
              <img src={Logo} className="middle-logo" alt="logo" />
            </div>
            <Link to="#" className="home-link" onClick={() => fetchSubjects()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>
          <div className="login-page-items column align-center scroll-fixed">
            <h2 className="manage-page-heading caps size4">Manage Subjects</h2>
            <div className="row justify-center align-center gap-15 add-buttons">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Subject</button>
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>Bulk Import</button>
            </div>
            <input
              type="text"
              placeholder="Search by name or code"
              className="input-field1"
              value={searchSubject}
              onChange={(e) => setSearchSubject(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {isFetchingSubjects ? (
                  <p className="loading-text">Loading subjects...</p>
                ) : subjects.length === 0 ? (
                  <p className="loading-text">No subjects available</p>
                ) : (
                  filteredSubjects.map((subjectItem) => (
                    <div key={subjectItem.id} className="class-item">
                      <p>{subjectItem.name} ({subjectItem.code})</p>
                      <div className="row align-center justify-center gap-15">
                        <button className="list-btn" onClick={() => handleEditSubject(subjectItem)}><i className="fas fa-edit"></i></button>
                        {/* <button className="list-btn" onClick={() => openDeleteModal(subjectItem)}><i className="fas fa-trash-alt"></i></button> */}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Subject Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Subject"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Subject</h2>
        <form className="subject-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Subject Name"
            value={newSubject.name}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="code"
            className="input-field"
            placeholder="Subject Code"
            value={newSubject.code}
            onChange={handleInputChange}
          />
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddSubject}>Add Subject</button>
          </div>
        </form>
      </Modal>

      {/* Edit Subject Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewSubject({ name: '', code: '' }); // Reset newSubject
          setEditingSubject(null); // Reset editingSubject
        }}
        contentLabel="Edit Subject"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Subject</h2>
        <form className="subject-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Subject Name"
            value={newSubject.name}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="code"
            className="input-field"
            placeholder="Subject Code"
            value={newSubject.code}
            onChange={handleInputChange}
          />          
          <div className="row align-center justify-center gap-1">            
            <button className="login-button" type="button" onClick={() => {
                setIsEditModalOpen(false);
                setNewSubject({ name: '', code: '' }); // Reset newSubject
                setEditingSubject(null); // Reset editingSubject
              }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateSubject}>Update Subject</button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Subject"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Subject</h2>
        {currentSubject && (
          <div>
            <p className="male-female-stat">Are you sure you want to delete <strong>{currentSubject.name}</strong>?</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button" type="button" onClick={() => handleDeleteSubject(currentSubject.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        isOpen={isBulkImportModalOpen}
        onRequestClose={() => {
          setIsBulkImportModalOpen(false);
          setFile(null);
          setImportProgress(0);
          setImportStatus('');
        }}
        contentLabel="Bulk Import Subjects"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Bulk Import Subjects</h2>
        <div className="modal-form">
          <div style={{marginBottom: '5px'}}>
            <label>
              <input
                type="radio"
                name="fileType"
                value="excel"
                checked={fileType === 'excel'}
                onChange={handleFileTypeChange}
              /> Excel File (.xlsx)
            </label>
            <label>
              <input
                type="radio"
                name="fileType"
                value="csv"
                checked={fileType === 'csv'}
                onChange={handleFileTypeChange}
              /> CSV File (.csv)
            </label>
          </div>
          
          <input
            type="file"
            accept={fileType === 'excel' ? '.xlsx, .xls' : '.csv'}
            onChange={handleFileChange}
            className="input-field1"
          />
          
          {importStatus && (
            <div>
              <p>{importStatus}</p>
              {importProgress > 0 && (
                <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '5px' }}>
                  <div
                    style={{
                      width: `${importProgress}%`,
                      height: '20px',
                      backgroundColor: '#4CAF50',
                      borderRadius: '5px',
                      textAlign: 'center',
                      lineHeight: '20px',
                      color: 'white'
                    }}
                  >
                    {importProgress}%
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="row align-center justify-center gap-15">
            <button
              type="button"
              className="login-button caps"
              onClick={() => {
                setIsBulkImportModalOpen(false);
                setFile(null);
                setImportProgress(0);
                setImportStatus('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button caps"
              onClick={processBulkImport}
              disabled={!file || update}
            >
              Import
            </button>
          </div>
          
          <div className="import-instructions">
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED FIELDS:</strong> name, code</p>
            <p><strong>FOR EXCEL:</strong> First row should contain column headers</p>
            <p><strong>FOR CSV:</strong> First row should contain column headers, comma-separated</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Subjects;